// Timezone-aware appointment formatting. Appointments store an absolute UTC instant
// (`starts_at_utc`); we render it in the user's PREFERRED timezone (chosen in their profile,
// defaulting to their device zone). Falls back to raw date/time for older records.

const TZ_KEY = "rc_pref_tz";

export function deviceTz() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; }
  catch { return "UTC"; }
}

// The timezone used for all display + for interpreting times the user types when scheduling.
export function getPreferredTz() {
  try { return localStorage.getItem(TZ_KEY) || deviceTz(); }
  catch { return deviceTz(); }
}

export function setPreferredTz(tz) {
  try {
    if (tz) localStorage.setItem(TZ_KEY, tz);
    window.dispatchEvent(new Event("rc-tz-changed"));
  } catch { /* storage unavailable */ }
}

// Scheduler entries are interpreted in the scheduler's preferred display timezone.
export function localTz() { return getPreferredTz(); }

// Format any ISO timestamp as "24 Jul 2026, 14:30" in the user's preferred timezone.
export function fmtStamp(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString("en-GB", {
      timeZone: getPreferredTz(), day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return ""; }
}

export function apptStartMs(a) {
  if (a?.starts_at_utc) {
    const t = new Date(a.starts_at_utc).getTime();
    if (!isNaN(t)) return t;
  }
  const t = new Date(`${a?.date}T${a?.time}`).getTime();
  return isNaN(t) ? 0 : t;
}

// Full "22 Jul 2026 · 2:00 PM SAST" in the user's preferred timezone.
export function fmtAppt(a) {
  if (a?.starts_at_utc) {
    const d = new Date(a.starts_at_utc);
    if (!isNaN(d.getTime())) {
      const tz = getPreferredTz();
      try {
        const date = d.toLocaleDateString(undefined, { timeZone: tz, year: "numeric", month: "short", day: "numeric" });
        const time = d.toLocaleTimeString(undefined, { timeZone: tz, hour: "numeric", minute: "2-digit", timeZoneName: "short" });
        return `${date} · ${time}`;
      } catch { /* invalid tz — fall through to device default */ }
    }
  }
  return `${a?.date || ""}${a?.time ? ` at ${a.time}` : ""}`.trim();
}

// Time only, "2:00 PM SAST", in the user's preferred timezone.
export function fmtApptTime(a) {
  if (a?.starts_at_utc) {
    const d = new Date(a.starts_at_utc);
    if (!isNaN(d.getTime())) {
      const tz = getPreferredTz();
      try { return d.toLocaleTimeString(undefined, { timeZone: tz, hour: "numeric", minute: "2-digit", timeZoneName: "short" }); }
      catch { /* fall through */ }
    }
  }
  return a?.time || "";
}
